<script lang="ts" setup>
import { computed, toRefs } from 'vue';
import VContentBox from '@shared/component/VContentBox/VContentBox.vue';
import VInfoBox from '@shared/component/VInfoBox/VInfoBox.vue';
import VSkeleton from '@shared/component/VSkeleton/VSkeleton.vue';
import { FontAwesomeIcon } from '@shared/plugin/icons';
import { useQuery } from '@tanstack/vue-query';
import { usePolisGetPolissen } from '@services/polis-public/orval-api/polis-api/polis-api';
import { polisQueries } from '../polisQueries';

const props = defineProps<{
  polisnummer: string;
  relatienummer: number;
}>();

const { polisnummer, relatienummer } = toRefs(props);

const { isPending, data: polissen } = usePolisGetPolissen(
  computed(() => ({
    relatienummer: relatienummer.value,
    neemRoyementenMee: true,
    neemBeursPakkettenMee: true,
  })),
  {
    query: {
      select: (result) => result.result,
      staleTime: 5 * 60 * 1000,
    },
  },
);

const pakketpolis = computed(() => {
  if (isPending.value || !polissen.value) return undefined;

  return polissen.value.find((polis) =>
    polis.subpolissen.some((sub) => sub.polisnummer === polisnummer.value),
  );
});

const { data: pakketpolisDetails, isPending: pakketpolisDetailsPending } = useQuery({
  ...polisQueries.getDetails(computed(() => pakketpolis.value!.polisnummer)),
  select: (res) => res.data,
  staleTime: 60000,
  enabled: () => !!pakketpolis.value?.polisnummer,
});

const { data } = useQuery({
  ...polisQueries.getDetails(polisnummer),
  select: (res) => res.data,
  staleTime: 60000,
});

const subpolis = computed(() =>
  data?.value?.polis?.subpolis?.length ? data.value.polis.subpolis[0] : undefined,
);

const verzekering = computed(() =>
  subpolis.value ? subpolis.value[data.value.polis.soortverzekering.toLowerCase()][0] : undefined,
);

const condities = computed(() =>
  (data?.value.polis?.conditie ?? [])
    .concat(pakketpolisDetails.value?.polis?.conditie ?? [])
    .concat(subpolis.value?.conditie ?? [])
    .concat(verzekering.value?.conditie ?? [])
    .concat(
      ...(verzekering.value?.dekking?.filter((d) => d.conditie).map((d) => d.conditie) ?? []),
    ),
);

const uniekeCondities = computed(() => new Set(condities.value.map((d) => d.internconditienummer)));
</script>
<template>
  <div class="flex flex-col gap-4">
    <VSkeleton v-if="pakketpolisDetailsPending" height="xl" />
    <VInfoBox v-if="!condities.length" type="info">
      Deze subpolis heeft geen voorwaarden.
    </VInfoBox>
    <div v-else class="flex flex-col gap-4">
      <a
        v-for="conditie in uniekeCondities"
        :key="conditie"
        :href="`/document/push/?voorwaardeid=${conditie}`"
        target="_blank"
        rel="noopener noreferrer"
      >
        <VContentBox :key="conditie" :margin-bottom="0">
          <div class="flex items-center gap-4">
            <FontAwesomeIcon size="lg" icon="fas fa-file-pdf" class="text-red" />
            <div>{{ condities.find((x) => x.internconditienummer === conditie).titel }}</div>
          </div>
        </VContentBox>
      </a>
    </div>
  </div>
</template>
